import { useEffect, useMemo, useState } from "react"
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material"
import AddIcon from "@mui/icons-material/Add"
import InventoryIcon from "@mui/icons-material/Inventory"
import WarehouseIcon from "@mui/icons-material/Warehouse"
import CheckCircleIcon from "@mui/icons-material/CheckCircle"
import LocalShippingIcon from "@mui/icons-material/LocalShipping"
import MainLayout from "../../layouts/MainLayout"
import api from "../../services/api"
import {
  approveKinMousseStockOrder,
  approveMaisonDjoStockOrder,
  createStockOrder,
  getStockOrderById,
  getStockOrderFormData,
  getStockOrders,
  receiveStockOrder,
  rejectStockOrder,
} from "../../services/stockOrderService"
import { getDeliveryTripFormData } from "../../services/deliveryTripService"
import { getBusinessLabel } from "../../config/appConfig"

const MAIN_COMPANY = getBusinessLabel("mainCompany", "Entreprise principale")
const MAIN_COMPANY_SHORT = getBusinessLabel("mainCompanyShort", MAIN_COMPANY)

const MAIN_DISTRIBUTOR = getBusinessLabel(
  "mainDistributor",
  "Distributeur principal"
)

const RELAY_DEPOT = getBusinessLabel("relayDepot", "Structure secondaire")
const RELAY_DEPOTS = getBusinessLabel("relayDepots", "Structures secondaires")

const SHOP = getBusinessLabel("shop", "Shop")
const SHOPS = getBusinessLabel("shops", "Shops")

const WAREHOUSE = getBusinessLabel("warehouse", "Entrepôt")
const SOURCE_WAREHOUSE = getBusinessLabel(
  "sourceWarehouse",
  `Entrepôt source ${MAIN_COMPANY.toLowerCase()}`
)

const COMPANY_PRICE = getBusinessLabel("companyPrice", "Prix entreprise")
const SHOP_PRICE = getBusinessLabel("shopPrice", "Prix shop")

const SUPPLIER_DEBT = getBusinessLabel(
  "supplierDebt",
  `Dette envers ${MAIN_COMPANY.toLowerCase()}`
)

const VALIDATION_COMPANY = getBusinessLabel(
  "validationCompany",
  `Validation ${MAIN_COMPANY.toLowerCase()}`
)

const VALIDATION_DISTRIBUTOR = getBusinessLabel(
  "validationDistributor",
  `Validation ${MAIN_DISTRIBUTOR.toLowerCase()}`
)

const VALIDATION_RELAY = getBusinessLabel(
  "validationRelay",
  `Validation ${RELAY_DEPOT.toLowerCase()}`
)

const statusLabels = {
  pending_maison_djo: `En attente ${MAIN_DISTRIBUTOR}`,
  pending_kin_mousse: `En attente ${MAIN_COMPANY}`,
  approved_by_maison_djo: `Validée ${MAIN_DISTRIBUTOR}`,
  approved_by_kin_mousse: `Validée ${MAIN_COMPANY}`,
  shipped: "Expédiée",
  received_by_shop: `Reçue par le ${SHOP.toLowerCase()}`,
  rejected: "Rejetée",
  rejected_by_maison_djo: `Rejetée ${MAIN_DISTRIBUTOR}`,
  rejected_by_kin_mousse: `Rejetée ${MAIN_COMPANY}`,
  cancelled: "Annulée",
}

const statusColors = {
  pending_maison_djo: "warning",
  pending_kin_mousse: "info",
  approved_by_maison_djo: "success",
  approved_by_kin_mousse: "primary",
  shipped: "secondary",
  received_by_shop: "success",
  rejected: "error",
  rejected_by_maison_djo: "error",
  rejected_by_kin_mousse: "error",
  cancelled: "default",
}

const statusGroupOptions = [
  {
    value: "all",
    label: "Toutes",
    description: "Toutes les commandes visibles",
    statuses: [],
  },
  {
    value: "pending",
    label: "En attente",
    description: "Commandes encore en validation",
    statuses: ["pending_maison_djo", "pending_kin_mousse"],
  },
  {
    value: "approved",
    label: "Validées",
    description: "Validées mais pas encore expédiées",
    statuses: ["approved_by_maison_djo", "approved_by_kin_mousse"],
  },
  {
    value: "shipped",
    label: "À recevoir",
    description: `Expédiées, réception ${SHOP.toLowerCase()} attendue`,
    statuses: ["shipped"],
  },
  {
    value: "received",
    label: "Historique reçues",
    description: `Commandes déjà reçues par le ${SHOP.toLowerCase()}`,
    statuses: ["received_by_shop"],
  },
  {
    value: "rejected",
    label: "Rejetées / annulées",
    description: "Commandes clôturées sans réception",
    statuses: [
      "rejected",
      "rejected_by_maison_djo",
      "rejected_by_kin_mousse",
      "cancelled",
    ],
  },
]

const closedHistoryStatuses = [
  "received_by_shop",
  "rejected",
  "rejected_by_maison_djo",
  "rejected_by_kin_mousse",
  "cancelled",
]

const MAISON_DJO_ROLES = [
  "admin_maison_djo",
  "gestionnaire_maison_djo",
  "stock_maison_djo",
  "livreur_maison_djo",
]

const DEPOT_RELAIS_ROLES = [
  "admin_depot_relais",
  "gestionnaire_depot_relais",
  "stock_depot_relais",
]

const SHOP_ROLES = [
  "gerant_magasin",
  "gerant_depot_relais",
]

const KIN_MOUSSE_ROLES = [
  "platform_admin",
  "super_admin",
  "dg",
  "gestionnaire_stock",
  "gestionnaire_operations",
]

const DEFAULT_FUEL_PRICE_CDF = 3500
const DEFAULT_EXCHANGE_RATE_CDF_USD = 2800
const ROWS_PER_PAGE = 10

function formatAmount(value) {
  return Number(value || 0).toLocaleString("fr-FR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

function hasNumericValue(value) {
  return value !== null && value !== undefined && value !== "" && Number(value) > 0
}

function formatCurrency(value, currency = "USD") {
  if (!hasNumericValue(value)) return "-"

  const digits = currency === "CDF" ? 0 : 2

  return `${Number(value || 0).toLocaleString("fr-FR", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  })} ${currency}`
}

function formatUsdCdf(usdValue, cdfValue) {
  const usd = hasNumericValue(usdValue) ? formatCurrency(usdValue, "USD") : "-"
  const cdf = hasNumericValue(cdfValue) ? formatCurrency(cdfValue, "CDF") : ""

  return cdf ? `${usd} / ${cdf}` : usd
}

function PriceStack({ usd, cdf }) {
  return (
    <Stack spacing={0.2}>
      <Typography variant="body2" fontWeight={800}>
        {formatCurrency(usd, "USD")}
      </Typography>
      {hasNumericValue(cdf) && (
        <Typography variant="caption" color="text.secondary">
          {formatCurrency(cdf, "CDF")}
        </Typography>
      )}
    </Stack>
  )
}

function getProductSearchLabel(product) {
  if (!product) return ""
  return `${product.code || ""} ${product.name || ""} ${product.price_currency || ""}`
}

function getProductShopPriceCdf(product) {
  return (
    product?.display_shop_selling_price_cdf ||
    product?.shop_selling_price_cdf ||
    product?.visible_unit_price_cdf ||
    null
  )
}

function getProductSupplierPriceCdf(product) {
  return (
    product?.display_supplier_unit_price_cdf ||
    product?.supplier_unit_price_cdf ||
    null
  )
}

function renderProductOption(props, product, canSeeSupplierPrices) {
  const { key, ...optionProps } = props
  const shopPrice = product?.shop_selling_price || product?.visible_unit_price || 0
  const supplierPrice = product?.supplier_unit_price || 0

  return (
    <Box component="li" key={key} {...optionProps}>
      <Stack spacing={0.3} sx={{ width: "100%" }}>
        <Typography fontWeight={900}>
          {product.code} - {product.name}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {SHOP_PRICE} : {formatUsdCdf(shopPrice, getProductShopPriceCdf(product))}
          {canSeeSupplierPrices
            ? ` • ${COMPANY_PRICE} : ${formatUsdCdf(
                supplierPrice,
                getProductSupplierPriceCdf(product)
              )}`
            : ""}
        </Typography>
      </Stack>
    </Box>
  )
}

function findProduct(products, productId) {
  return (
    products.find((product) => String(product.id) === String(productId)) || null
  )
}

function formatQuantity(value) {
  return Number(value || 0).toLocaleString("fr-FR", {
    maximumFractionDigits: 2,
  })
}

function formatDate(value) {
  if (!value) return "-"

  const date = new Date(value)

  if (Number.isNaN(date.getTime())) return "-"

  return date.toLocaleDateString("fr-FR")
}

function formatDateTime(value) {
  if (!value) return "-"

  const date = new Date(value)

  if (Number.isNaN(date.getTime())) return "-"

  return date.toLocaleString("fr-FR")
}

function calculateFuel(
  distanceKm,
  consumptionL100Km,
  fuelPricePerLiterCdf,
  exchangeRateCdfUsd
) {
  const distance = Number(distanceKm || 0)
  const consumption = Number(consumptionL100Km || 0)
  const priceCdf = Number(fuelPricePerLiterCdf || 0)
  const exchangeRate = Number(exchangeRateCdfUsd || 0)

  const liters = (distance * consumption) / 100
  const costCdf = liters * priceCdf
  const costUsd = exchangeRate > 0 ? costCdf / exchangeRate : 0

  return {
    liters,
    cost: costCdf,
    costCdf,
    costUsd,
  }
}

function getCurrentContext() {
  return {
    role: localStorage.getItem("kongosys_role") || "",
    storeId: localStorage.getItem("kongosys_store_id") || "",
    storeName: localStorage.getItem("kongosys_store_name") || "",
    organizationId: localStorage.getItem("kongosys_organization_id") || "",
    organizationCode: localStorage.getItem("kongosys_organization_code") || "",
    organizationName: localStorage.getItem("kongosys_organization_name") || "",
  }
}

function getApiErrorMessage(error) {
  const status = error?.response?.status
  const data = error?.response?.data
  const detail = data?.detail

  if (typeof detail === "string") {
    return detail
  }

  if (Array.isArray(detail)) {
    return detail
      .map((item) => {
        const field = Array.isArray(item.loc) ? item.loc.join(".") : ""
        return `${field ? field + " : " : ""}${
          item.msg || JSON.stringify(item)
        }`
      })
      .join(" | ")
  }

  if (detail && typeof detail === "object") {
    return JSON.stringify(detail)
  }

  if (typeof data === "string") {
    return data
  }

  if (status) {
    return `Erreur backend ${status}. Vérifiez le terminal FastAPI pour le détail.`
  }

  return error?.message || "Action impossible"
}

function normalizeDetailPayload(data) {
  if (!data) {
    return {
      order: null,
      items: [],
      history: [],
    }
  }

  if (data.order) {
    return {
      order: data.order,
      items: Array.isArray(data.items) ? data.items : [],
      history: Array.isArray(data.history) ? data.history : [],
    }
  }

  const { items, history, ...order } = data

  return {
    order,
    items: Array.isArray(items) ? items : [],
    history: Array.isArray(history) ? history : [],
  }
}

function getOrderStoreCode(order) {
  return order?.request_store_code || order?.store_code || "-"
}

function getOrderStoreName(order) {
  return order?.request_store_name || order?.store_name || "-"
}

function getOrderOrganizationCode(order) {
  return order?.requester_organization_code || order?.organization_code || ""
}

function getOrderOrganizationName(order) {
  return (
    order?.requester_organization_name ||
    order?.organization_name ||
    order?.organization_code ||
    "-"
  )
}

function getOrderOrganizationType(order) {
  return order?.requester_organization_type || order?.organization_type || ""
}

function isDistributorOrder(order) {
  return (
    getOrderOrganizationCode(order) === "MAISON-DJO" ||
    getOrderOrganizationType(order) === "distributor"
  )
}

function isRelayDepotOrder(order) {
  return (
    getOrderOrganizationCode(order) === "DEPOT-RELAIS" ||
    getOrderOrganizationType(order) === "relay_depot"
  )
}

function isClosedOrderStatus(status) {
  return [
    "received_by_shop",
    "rejected",
    "rejected_by_maison_djo",
    "rejected_by_kin_mousse",
    "cancelled",
  ].includes(status)
}

function getOrderRequestedQuantity(order) {
  return Number(order?.requested_quantity ?? order?.total_quantity ?? 0)
}

function getOrderReceivedQuantity(order) {
  return Number(order?.received_quantity ?? 0)
}

function getOrderRemainingQuantity(order) {
  if (isClosedOrderStatus(order?.status)) {
    return 0
  }

  const backendValue = order?.remaining_quantity

  if (backendValue !== null && backendValue !== undefined) {
    return Math.max(Number(backendValue || 0), 0)
  }

  return Math.max(
    getOrderRequestedQuantity(order) - getOrderReceivedQuantity(order),
    0
  )
}

function getOrderQuantity(order) {
  return getOrderRequestedQuantity(order)
}

function getOrderApprovedQuantity(order) {
  return Number(
    order?.kin_mousse_approved_quantity ??
      order?.maison_djo_approved_quantity ??
      0
  )
}

function getOrderShippedQuantity(order) {
  return Number(order?.shipped_quantity ?? 0)
}

function getOrderShopAmount(order) {
  return Number(
    order?.total_shop_selling_amount ??
      order?.shop_total_amount ??
      order?.shop_selling_amount ??
      0
  )
}

function getOrderReceivedShopValue(order) {
  const backendValue = order?.received_shop_value

  if (backendValue !== null && backendValue !== undefined) {
    return Math.max(Number(backendValue || 0), 0)
  }

  const requestedQuantity = getOrderRequestedQuantity(order)
  const receivedQuantity = getOrderReceivedQuantity(order)
  const totalShopAmount = getOrderShopAmount(order)

  if (requestedQuantity <= 0) {
    return 0
  }

  return Math.max(
    (Math.min(receivedQuantity, requestedQuantity) / requestedQuantity) *
      totalShopAmount,
    0
  )
}

function getOrderRemainingShopValue(order) {
  if (isClosedOrderStatus(order?.status)) {
    return 0
  }

  const backendValue = order?.remaining_shop_value

  if (backendValue !== null && backendValue !== undefined) {
    return Math.max(Number(backendValue || 0), 0)
  }

  return Math.max(
    getOrderShopAmount(order) - getOrderReceivedShopValue(order),
    0
  )
}

function getOrderSupplierAmount(order) {
  return Number(
    order?.total_supplier_amount ??
      order?.supplier_total_amount ??
      order?.supplier_amount ??
      0
  )
}

function getOrderMargin(order) {
  return Number(order?.total_expected_margin ?? order?.expected_margin ?? 0)
}

function getItemRemainingQuantity(item, orderStatus = "") {
  if (isClosedOrderStatus(orderStatus)) {
    return 0
  }

  if (
    item?.remaining_quantity !== null &&
    item?.remaining_quantity !== undefined
  ) {
    return Math.max(Number(item.remaining_quantity || 0), 0)
  }

  return Math.max(
    Number(item?.requested_quantity || 0) -
      Number(item?.received_quantity || 0),
    0
  )
}

function getItemReceivedShopValue(item) {
  if (
    item?.received_shop_value !== null &&
    item?.received_shop_value !== undefined
  ) {
    return Math.max(Number(item.received_shop_value || 0), 0)
  }

  return (
    Number(item?.received_quantity || 0) *
    Number(item?.shop_selling_price || 0)
  )
}

function getItemRemainingShopValue(item, orderStatus = "") {
  if (isClosedOrderStatus(orderStatus)) {
    return 0
  }

  if (
    item?.remaining_shop_value !== null &&
    item?.remaining_shop_value !== undefined
  ) {
    return Math.max(Number(item.remaining_shop_value || 0), 0)
  }

  return (
    getItemRemainingQuantity(item, orderStatus) *
    Number(item?.shop_selling_price || 0)
  )
}

function getOrderStatusGroup(order) {
  if (order?.status_group) return order.status_group

  const status = order?.status || ""

  const group = statusGroupOptions.find((item) =>
    item.statuses.includes(status)
  )

  return group?.value || "other"
}

function matchesStatusGroup(order, groupValue) {
  if (!groupValue || groupValue === "all") return true

  const group = statusGroupOptions.find((item) => item.value === groupValue)

  if (!group) return true

  return group.statuses.includes(order?.status)
}

function getWorkflowLabel(order) {
  const status = order?.status || ""

  if (status === "pending_maison_djo") {
    return VALIDATION_DISTRIBUTOR
  }

  if (status === "pending_kin_mousse") {
    return VALIDATION_COMPANY
  }

  if (
    status === "approved_by_maison_djo" ||
    status === "approved_by_kin_mousse"
  ) {
    return "Validée / attente expédition"
  }

  if (status === "shipped") {
    return "Expédiée / à recevoir"
  }

  if (status === "received_by_shop") {
    return "Historique reçu"
  }

  if (closedHistoryStatuses.includes(status)) {
    return "Historique clôturé"
  }

  return statusLabels[status] || status || "-"
}

function getWorkflowColor(order) {
  const status = order?.status || ""

  if (status === "shipped") return "secondary"
  if (status === "received_by_shop") return "success"
  if (closedHistoryStatuses.includes(status)) return "default"
  if (status.includes("pending")) return "warning"

  return "primary"
}

function getLastMovementLabel(order) {
  const lastStatus = order?.last_status || order?.status
  const lastLabel = statusLabels[lastStatus] || lastStatus || "Mouvement"
  const user = order?.last_changed_by_name || "Utilisateur"
  const date = formatDateTime(order?.last_changed_at || order?.updated_at)

  return {
    title: `${lastLabel} — ${user}`,
    date,
    notes: order?.last_notes || "",
  }
}

function countOrdersByGroup(orders, groupValue) {
  if (!groupValue || groupValue === "all") return orders.length

  return orders.filter((order) => matchesStatusGroup(order, groupValue)).length
}

function getBusinessName(current) {
  if (current.organizationCode === "DEPOT-RELAIS") {
    return RELAY_DEPOT
  }

  if (current.organizationCode === "MAISON-DJO") {
    return MAIN_DISTRIBUTOR
  }

  if (DEPOT_RELAIS_ROLES.includes(current.role)) {
    return RELAY_DEPOT
  }

  if (MAISON_DJO_ROLES.includes(current.role)) {
    return MAIN_DISTRIBUTOR
  }

  return ""
}


function MobileStockOrderCard({
  order,
  isShopUser,
  saving,
  canValidateDistributor,
  canValidateCompany,
  canShipOrder,
  canReceiveOrder,
  canRejectOrder,
  onDetail,
  onValidateDistributor,
  onValidateCompany,
  onShip,
  onReceive,
  onReject,
}) {
  const lastMovement = getLastMovementLabel(order)

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.6,
        borderRadius: 3.5,
        boxShadow: "0 6px 18px rgba(15, 23, 42, 0.04)",
      }}
    >
      <Stack spacing={1.5}>
        <Stack direction="row" justifyContent="space-between" spacing={1}>
          <Box sx={{ minWidth: 0 }}>
            <Typography fontWeight={900} noWrap>
              {order.order_code || "-"}
            </Typography>

            <Typography variant="body2" color="text.secondary" noWrap>
              {getOrderStoreCode(order)} - {getOrderStoreName(order)}
            </Typography>

            {!isShopUser && (
              <Typography variant="caption" color="text.secondary">
                {getOrderOrganizationName(order)}
              </Typography>
            )}
          </Box>

          <Chip
            label={statusLabels[order.status] || order.status || "-"}
            color={statusColors[order.status] || "default"}
            size="small"
            sx={{
              maxWidth: 170,
              "& .MuiChip-label": {
                overflow: "hidden",
                textOverflow: "ellipsis",
              },
            }}
          />
        </Stack>

        {isShopUser && (
          <Chip
            label={getWorkflowLabel(order)}
            color={getWorkflowColor(order)}
            size="small"
            variant="outlined"
            sx={{
              alignSelf: "flex-start",
              maxWidth: "100%",
              "& .MuiChip-label": {
                overflow: "hidden",
                textOverflow: "ellipsis",
              },
            }}
          />
        )}

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 1.4,
          }}
        >
          {isShopUser ? (
            <>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Demandée
                </Typography>
                <Typography fontWeight={800}>
                  {formatQuantity(getOrderRequestedQuantity(order))}
                </Typography>
              </Box>

              <Box>
                <Typography variant="caption" color="text.secondary">
                  Validée
                </Typography>
                <Typography fontWeight={800}>
                  {formatQuantity(getOrderApprovedQuantity(order))}
                </Typography>
              </Box>

              <Box>
                <Typography variant="caption" color="text.secondary">
                  Expédiée
                </Typography>
                <Typography fontWeight={800}>
                  {formatQuantity(getOrderShippedQuantity(order))}
                </Typography>
              </Box>

              <Box>
                <Typography variant="caption" color="text.secondary">
                  Reçue
                </Typography>
                <Typography fontWeight={800}>
                  {formatQuantity(getOrderReceivedQuantity(order))}
                </Typography>
              </Box>

              <Box>
                <Typography variant="caption" color="text.secondary">
                  Restante
                </Typography>
                <Typography fontWeight={800}>
                  {formatQuantity(getOrderRemainingQuantity(order))}
                </Typography>
              </Box>

              <Box>
                <Typography variant="caption" color="text.secondary">
                  Valeur reçue
                </Typography>
                <Typography fontWeight={800}>
                  {formatAmount(getOrderReceivedShopValue(order))}
                </Typography>
              </Box>

              <Box sx={{ gridColumn: "1 / -1" }}>
                <Typography variant="caption" color="text.secondary">
                  Valeur restant à recevoir
                </Typography>
                <Typography fontWeight={900}>
                  {formatAmount(getOrderRemainingShopValue(order))}
                </Typography>
              </Box>
            </>
          ) : (
            <>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Quantité
                </Typography>
                <Typography fontWeight={800}>
                  {formatQuantity(getOrderQuantity(order))}
                </Typography>
              </Box>

              <Box>
                <Typography variant="caption" color="text.secondary">
                  Valeur vente
                </Typography>
                <Typography fontWeight={800}>
                  {formatAmount(getOrderShopAmount(order))}
                </Typography>
              </Box>

              <Box>
                <Typography variant="caption" color="text.secondary">
                  Dette attendue
                </Typography>
                <Typography fontWeight={800}>
                  {formatAmount(getOrderSupplierAmount(order))}
                </Typography>
              </Box>

              <Box>
                <Typography variant="caption" color="text.secondary">
                  Marge attendue
                </Typography>
                <Typography fontWeight={800}>
                  {formatAmount(getOrderMargin(order))}
                </Typography>
              </Box>
            </>
          )}

          <Box sx={{ gridColumn: "1 / -1" }}>
            <Typography variant="caption" color="text.secondary">
              Date
            </Typography>
            <Typography fontWeight={700}>{formatDate(order.created_at)}</Typography>
          </Box>
        </Box>

        <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
          <Typography variant="caption" color="text.secondary">
            Dernier mouvement
          </Typography>

          <Typography variant="body2" fontWeight={800}>
            {lastMovement.title}
          </Typography>

          <Typography variant="caption" color="text.secondary">
            {lastMovement.date}
          </Typography>

          {lastMovement.notes && (
            <Typography variant="body2" mt={0.5} whiteSpace="pre-line">
              {lastMovement.notes}
            </Typography>
          )}
        </Paper>

        <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", gap: 1 }}>
          <Button
            size="small"
            variant="contained"
            disabled={saving}
            onClick={onDetail}
            sx={{ flex: "1 1 120px" }}
          >
            Détail
          </Button>

          {canValidateDistributor && (
            <Button
              size="small"
              color="success"
              variant="outlined"
              disabled={saving}
              onClick={onValidateDistributor}
              sx={{ flex: "1 1 120px" }}
            >
              Valider
            </Button>
          )}

          {canValidateCompany && (
            <Button
              size="small"
              color="primary"
              variant="outlined"
              disabled={saving}
              onClick={onValidateCompany}
              sx={{ flex: "1 1 120px" }}
            >
              Valider
            </Button>
          )}

          {canShipOrder && (
            <Button
              size="small"
              color="secondary"
              variant="outlined"
              disabled={saving}
              onClick={onShip}
              sx={{ flex: "1 1 120px" }}
            >
              Expédier
            </Button>
          )}

          {canReceiveOrder && (
            <Button
              size="small"
              color="success"
              variant="contained"
              disabled={saving}
              onClick={onReceive}
              sx={{ flex: "1 1 120px" }}
            >
              Recevoir
            </Button>
          )}

          {canRejectOrder && (
            <Button
              size="small"
              color="error"
              variant="outlined"
              disabled={saving}
              onClick={onReject}
              sx={{ flex: "1 1 120px" }}
            >
              Rejeter
            </Button>
          )}
        </Stack>
      </Stack>
    </Paper>
  )
}


function MetricCard({
  label,
  value,
  caption,
  icon,
  accent = "#2563eb",
  background = "rgba(37, 99, 235, 0.09)",
}) {
  return (
    <Paper
      variant="outlined"
      sx={{
        px: { xs: 1.35, sm: 1.5 },
        py: 1.2,
        minWidth: 0,
        borderRadius: 3,
        borderColor: "rgba(148, 163, 184, 0.22)",
        bgcolor: "#ffffff",
        boxShadow: "0 6px 18px rgba(15, 23, 42, 0.035)",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.15,
          minWidth: 0,
        }}
      >
        <Box
          sx={{
            width: 42,
            height: 42,
            flexShrink: 0,
            borderRadius: 2.5,
            display: "grid",
            placeItems: "center",
            color: accent,
            bgcolor: background,
          }}
        >
          {icon}
        </Box>

        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            sx={{
              fontSize: { xs: 18, sm: 20 },
              lineHeight: 1.1,
              fontWeight: 900,
              color: "#0f172a",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {value}
          </Typography>

          <Typography
            variant="body2"
            sx={{
              mt: 0.2,
              fontWeight: 800,
              color: "#334155",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {label}
          </Typography>

          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: "block",
              mt: 0.15,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {caption}
          </Typography>
        </Box>
      </Box>
    </Paper>
  )
}


const rlsysPageSx = {
  minWidth: 0,
  pb: { xs: 2, md: 4 },

  "& .MuiTypography-h4": {
    color: "#0f172a",
    fontWeight: 900,
    letterSpacing: "-0.025em",
    lineHeight: 1.08,
    fontSize: {
      xs: "1.65rem",
      sm: "1.95rem",
      md: "2.15rem",
    },
  },

  "& .MuiPaper-root": {
    backgroundImage: "none",
    borderColor: "rgba(148, 163, 184, 0.24)",
  },

  "& .MuiButton-root": {
    minHeight: 40,
    borderRadius: 2.5,
    textTransform: "none",
    fontWeight: 800,
    boxShadow: "none",
  },

  "& .MuiButton-containedPrimary": {
    boxShadow: "0 8px 18px rgba(37, 99, 235, 0.16)",
  },

  "& .MuiOutlinedInput-root": {
    borderRadius: 2.5,
    backgroundColor: "#fff",
  },

  "& .MuiAlert-root": {
    borderRadius: 3.5,
  },

  "& .MuiChip-root": {
    fontWeight: 800,
  },

  "& .MuiTableHead-root .MuiTableCell-root": {
    backgroundColor: "#f8fafc",
    color: "#334155",
    fontWeight: 900,
    whiteSpace: "nowrap",
    borderBottomColor: "#e2e8f0",
  },

  "& .MuiTableBody-root .MuiTableRow-root:hover": {
    backgroundColor: "#f8fbff",
  },

  "& .MuiDataGrid-root": {
    border: 0,
    backgroundColor: "#fff",
  },

  "& .MuiDataGrid-columnHeaders": {
    backgroundColor: "#f8fafc",
    color: "#334155",
    fontWeight: 900,
    borderBottomColor: "#e2e8f0",
  },

  "& .MuiDataGrid-row:hover": {
    backgroundColor: "#f8fbff",
  },

  "& .MuiDataGrid-footerContainer": {
    borderTopColor: "#e2e8f0",
  },

  "& .MuiDialog-paper": {
    borderRadius: { xs: 0, sm: 3.5 },
  },
}


function StockOrdersPage() {
  const theme = useTheme()
  const isSmDown = useMediaQuery(theme.breakpoints.down("sm"))

  const current = getCurrentContext()

  const isShopUser = SHOP_ROLES.includes(current.role)
  const isMaisonDjoUser = MAISON_DJO_ROLES.includes(current.role)
  const isDepotRelaisUser = DEPOT_RELAIS_ROLES.includes(current.role)
  const isKinMousseUser = KIN_MOUSSE_ROLES.includes(current.role)

  const businessName = getBusinessName(current)
  const businessScopeLabel = businessName
    ? `${SHOPS.toLowerCase()} ${businessName}`
    : SHOPS.toLowerCase()

  const canSeeSupplierPrices = !isShopUser

  const orderTableColSpan = canSeeSupplierPrices ? 11 : 13
  const cartTableColSpan = canSeeSupplierPrices ? 9 : 6
  const detailTableMinWidth = canSeeSupplierPrices ? 1200 : 1100

  const [orders, setOrders] = useState([])
  const [stores, setStores] = useState([])
  const [products, setProducts] = useState([])
  const [warehouses, setWarehouses] = useState([])

  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)

  const [openCreate, setOpenCreate] = useState(false)
  const [openDetail, setOpenDetail] = useState(false)
  const [detail, setDetail] = useState({
    order: null,
    items: [],
    history: [],
  })

  const [openKinApproval, setOpenKinApproval] = useState(false)
  const [kinApprovalOrder, setKinApprovalOrder] = useState(null)
  const [kinApprovalItems, setKinApprovalItems] = useState([])
  const [kinApprovalNotes, setKinApprovalNotes] = useState("")

  const [openShipDialog, setOpenShipDialog] = useState(false)
  const [shippingOrder, setShippingOrder] = useState(null)
  const [shippingData, setShippingData] = useState({
    vehicles: [],
    drivers: [],
    warehouses: [],
  })
  const [shippingForm, setShippingForm] = useState({
    vehicle_id: "",
    driver_id: "",
    estimated_distance_km: "",
    fuel_price_per_liter: String(DEFAULT_FUEL_PRICE_CDF),
    fuel_price_per_liter_cdf: String(DEFAULT_FUEL_PRICE_CDF),
    exchange_rate_cdf_usd: String(DEFAULT_EXCHANGE_RATE_CDF_USD),
    notes: "",
  })

  const [statusFilter, setStatusFilter] = useState("")
  const [statusGroupFilter, setStatusGroupFilter] = useState("all")
  const [dateFrom, setDateFrom] = useState("")
  const [dateTo, setDateTo] = useState("")
  const [search, setSearch] = useState("")
  const [orderPage, setOrderPage] = useState(0)

  const [formData, setFormData] = useState({
    request_store_id: current.storeId || "",
    source_warehouse_id: "",
    notes: "",
  })

  const [line, setLine] = useState({
    product_id: "",
    requested_quantity: "1",
  })

  const [cart, setCart] = useState([])

  const [snackbar, setSnackbar] = useState({
    open: false,
    severity: "success",
    message: "",
  })

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({
      open: true,
      severity,
      message: String(message || ""),
    })
  }

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({
      ...prev,
      open: false,
    }))
  }

  const loadFormData = async () => {
    try {
      const data = await getStockOrderFormData()

      setStores(Array.isArray(data?.stores) ? data.stores : [])
      setProducts(Array.isArray(data?.products) ? data.products : [])
      setWarehouses(Array.isArray(data?.warehouses) ? data.warehouses : [])

      if (isShopUser && current.storeId) {
        setFormData((prev) => ({
          ...prev,
          request_store_id: current.storeId,
          source_warehouse_id: "",
        }))
      }
    } catch (error) {
      console.error(error)
      showSnackbar(getApiErrorMessage(error), "error")
    }
  }

  const loadOrders = async () => {
    try {
      setLoading(true)

      const params = {}

      if (statusFilter) params.status = statusFilter
      if (dateFrom) params.date_from = dateFrom
      if (dateTo) params.date_to = dateTo

      const data = await getStockOrders(params)

      setOrders(Array.isArray(data) ? data : [])
      setOrderPage(0)
    } catch (error) {
      console.error(error)
      showSnackbar(getApiErrorMessage(error), "error")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadFormData()
    loadOrders()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const selectedProduct = useMemo(() => {
    return findProduct(products, line.product_id)
  }, [products, line.product_id])

  const totals = useMemo(() => {
    return cart.reduce(
      (acc, item) => {
        acc.quantity += Number(item.requested_quantity || 0)
        acc.supplier += Number(item.supplier_total || 0)
        acc.shop += Number(item.shop_total || 0)
        acc.margin += Number(item.margin || 0)
        acc.supplierCdf += Number(item.supplier_total_cdf || 0)
        acc.shopCdf += Number(item.shop_total_cdf || 0)
        acc.marginCdf += Number(item.margin_cdf || 0)

        return acc
      },
      {
        quantity: 0,
        supplier: 0,
        shop: 0,
        margin: 0,
        supplierCdf: 0,
        shopCdf: 0,
        marginCdf: 0,
      }
    )
  }, [cart])

  const kinApprovalTotals = useMemo(() => {
    return kinApprovalItems.reduce(
      (acc, item) => {
        const approvedQuantity = Number(item.approved_quantity || 0)
        const supplierPrice = Number(item.supplier_unit_price || 0)
        const shopPrice = Number(item.shop_selling_price || 0)

        acc.quantity += approvedQuantity
        acc.supplier += approvedQuantity * supplierPrice
        acc.shop += approvedQuantity * shopPrice
        acc.margin += approvedQuantity * (shopPrice - supplierPrice)

        return acc
      },
      {
        quantity: 0,
        supplier: 0,
        shop: 0,
        margin: 0,
      }
    )
  }, [kinApprovalItems])

  const selectedShippingVehicle = useMemo(() => {
    return (
      shippingData.vehicles.find(
        (vehicle) => String(vehicle.id) === String(shippingForm.vehicle_id)
      ) || null
    )
  }, [shippingData.vehicles, shippingForm.vehicle_id])

  const selectedShippingDriver = useMemo(() => {
    return (
      shippingData.drivers.find(
        (driver) => String(driver.id) === String(shippingForm.driver_id)
      ) || null
    )
  }, [shippingData.drivers, shippingForm.driver_id])

  const shippingFuelEstimate = useMemo(() => {
    return calculateFuel(
      shippingForm.estimated_distance_km,
      selectedShippingVehicle?.fuel_consumption_l_100km,
      shippingForm.fuel_price_per_liter_cdf || shippingForm.fuel_price_per_liter,
      shippingForm.exchange_rate_cdf_usd
    )
  }, [
    selectedShippingVehicle,
    shippingForm.estimated_distance_km,
    shippingForm.fuel_price_per_liter,
    shippingForm.fuel_price_per_liter_cdf,
    shippingForm.exchange_rate_cdf_usd,
  ])

  const searchedOrders = useMemo(() => {
    const keyword = search.trim().toLowerCase()

    return orders.filter((order) => {
      if (!keyword) return true

      return (
        String(order.order_code || "").toLowerCase().includes(keyword) ||
        String(getOrderStoreCode(order)).toLowerCase().includes(keyword) ||
        String(getOrderStoreName(order)).toLowerCase().includes(keyword) ||
        String(getOrderOrganizationName(order)).toLowerCase().includes(keyword) ||
        String(order.status || "").toLowerCase().includes(keyword) ||
        String(order.last_changed_by_name || "").toLowerCase().includes(keyword) ||
        String(order.last_notes || "").toLowerCase().includes(keyword)
      )
    })
  }, [orders, search])

  const filteredOrders = useMemo(() => {
    return searchedOrders.filter((order) =>
      matchesStatusGroup(order, statusGroupFilter)
    )
  }, [searchedOrders, statusGroupFilter])

  const paginatedOrders = useMemo(() => {
    const start = orderPage * ROWS_PER_PAGE
    return filteredOrders.slice(start, start + ROWS_PER_PAGE)
  }, [filteredOrders, orderPage])

  useEffect(() => {
    setOrderPage(0)
  }, [search, statusFilter, statusGroupFilter, dateFrom, dateTo])

  useEffect(() => {
    const totalPages = Math.max(
      1,
      Math.ceil(filteredOrders.length / ROWS_PER_PAGE)
    )

    if (orderPage >= totalPages) {
      setOrderPage(totalPages - 1)
    }
  }, [filteredOrders.length, orderPage])

  const statusGroupCounts = useMemo(() => {
    return statusGroupOptions.reduce((acc, option) => {
      acc[option.value] = countOrdersByGroup(searchedOrders, option.value)
      return acc
    }, {})
  }, [searchedOrders])

  const shopOrderSummary = useMemo(() => {
    return filteredOrders.reduce(
      (acc, order) => {
        acc.requestedQuantity += getOrderRequestedQuantity(order)
        acc.receivedQuantity += getOrderReceivedQuantity(order)
        acc.remainingQuantity += getOrderRemainingQuantity(order)
        acc.receivedValue += getOrderReceivedShopValue(order)
        acc.remainingValue += getOrderRemainingShopValue(order)

        return acc
      },
      {
        requestedQuantity: 0,
        receivedQuantity: 0,
        remainingQuantity: 0,
        receivedValue: 0,
        remainingValue: 0,
      }
    )
  }, [filteredOrders])

  const loadShippingData = async () => {
    try {
      const data = await getDeliveryTripFormData()

      setShippingData({
        vehicles: Array.isArray(data?.vehicles) ? data.vehicles : [],
        drivers: Array.isArray(data?.drivers) ? data.drivers : [],
        warehouses: Array.isArray(data?.warehouses) ? data.warehouses : [],
      })

      return data
    } catch (error) {
      console.error(error)
      showSnackbar(getApiErrorMessage(error), "error")
      return null
    }
  }

  const resetCreateForm = () => {
    setFormData({
      request_store_id: current.storeId || "",
      source_warehouse_id: "",
      notes: "",
    })

    setLine({
      product_id: "",
      requested_quantity: "1",
    })

    setCart([])
  }

  const openCreateDialog = () => {
    resetCreateForm()
    setOpenCreate(true)
  }

  const closeCreateDialog = () => {
    if (saving) return
    setOpenCreate(false)
  }

  const resetFilters = async () => {
    setSearch("")
    setStatusFilter("")
    setStatusGroupFilter("all")
    setDateFrom("")
    setDateTo("")
    setOrderPage(0)

    try {
      setLoading(true)
      const data = await getStockOrders({})
      setOrders(Array.isArray(data) ? data : [])
    } catch (error) {
      console.error(error)
      showSnackbar(getApiErrorMessage(error), "error")
    } finally {
      setLoading(false)
    }
  }

  const addItem = () => {
    if (!line.product_id) {
      showSnackbar("Sélectionnez un produit", "warning")
      return
    }

    if (!selectedProduct) {
      showSnackbar("Produit introuvable", "warning")
      return
    }

    const quantity = Number(line.requested_quantity || 0)

    if (quantity <= 0) {
      showSnackbar("La quantité doit être supérieure à 0", "warning")
      return
    }

    const exists = cart.find(
      (item) => String(item.product_id) === String(line.product_id)
    )

    if (exists) {
      showSnackbar("Ce produit est déjà dans la commande", "warning")
      return
    }

    const supplierPrice = canSeeSupplierPrices
      ? Number(selectedProduct?.supplier_unit_price || 0)
      : null

    const shopPrice = Number(
      selectedProduct?.shop_selling_price ||
        selectedProduct?.visible_unit_price ||
        0
    )

    const supplierPriceCdf = canSeeSupplierPrices
      ? Number(getProductSupplierPriceCdf(selectedProduct) || 0)
      : null
    const shopPriceCdf = Number(getProductShopPriceCdf(selectedProduct) || 0)

    const shopTotal = shopPrice * quantity
    const supplierTotal = canSeeSupplierPrices ? supplierPrice * quantity : null
    const margin = canSeeSupplierPrices ? shopTotal - supplierTotal : null

    const shopTotalCdf = shopPriceCdf > 0 ? shopPriceCdf * quantity : null
    const supplierTotalCdf =
      canSeeSupplierPrices && supplierPriceCdf > 0
        ? supplierPriceCdf * quantity
        : null
    const marginCdf =
      canSeeSupplierPrices && shopTotalCdf !== null && supplierTotalCdf !== null
        ? shopTotalCdf - supplierTotalCdf
        : null

    setCart((prev) => [
      ...prev,
      {
        product_id: Number(line.product_id),
        product_code: selectedProduct?.code || "",
        product_name: selectedProduct?.name || "",
        requested_quantity: quantity,
        supplier_unit_price: supplierPrice,
        supplier_unit_price_cdf: supplierPriceCdf,
        shop_selling_price: shopPrice,
        shop_selling_price_cdf: shopPriceCdf,
        supplier_total: supplierTotal,
        supplier_total_cdf: supplierTotalCdf,
        shop_total: shopTotal,
        shop_total_cdf: shopTotalCdf,
        margin,
        margin_cdf: marginCdf,
      },
    ])

    setLine({
      product_id: "",
      requested_quantity: "1",
    })
  }

  const removeItem = (productId) => {
    setCart((prev) => prev.filter((item) => item.product_id !== productId))
  }

  const handleCreate = async () => {
    if (!formData.request_store_id) {
      showSnackbar(`${SHOP} obligatoire`, "warning")
      return
    }

    if (cart.length === 0) {
      showSnackbar("Ajoutez au moins un produit", "warning")
      return
    }

    const payload = {
      request_store_id: Number(formData.request_store_id),
      source_warehouse_id: formData.source_warehouse_id
        ? Number(formData.source_warehouse_id)
        : null,
      notes: formData.notes?.trim() || null,
      items: cart.map((item) => ({
        product_id: Number(item.product_id),
        requested_quantity: Number(item.requested_quantity),
      })),
    }

    try {
      setSaving(true)

      const result = await createStockOrder(payload)

      showSnackbar(
        result?.order_code
          ? `Commande créée : ${result.order_code}`
          : result?.message || "Commande créée avec succès",
        "success"
      )

      setOpenCreate(false)
      resetCreateForm()
      await loadOrders()
    } catch (error) {
      console.error(error)
      showSnackbar(getApiErrorMessage(error), "error")
    } finally {
      setSaving(false)
    }
  }

  const openOrderDetail = async (orderId) => {
    try {
      const data = await getStockOrderById(orderId)
      const normalized = normalizeDetailPayload(data)

      setDetail(normalized)
      setOpenDetail(true)
    } catch (error) {
      console.error(error)
      showSnackbar(getApiErrorMessage(error), "error")
    }
  }

  const runAction = async (label, action) => {
    const notes = window.prompt(`${label} - commentaire optionnel :`)

    if (notes === null) {
      return
    }

    try {
      setSaving(true)

      const result = await action(notes)

      showSnackbar(result?.message || `${label} effectué avec succès`, "success")

      await loadOrders()

      if (detail?.order?.id) {
        const fresh = await getStockOrderById(detail.order.id)
        setDetail(normalizeDetailPayload(fresh))
      }
    } catch (error) {
      console.error(error)
      showSnackbar(getApiErrorMessage(error), "error")
    } finally {
      setSaving(false)
    }
  }

  const openKinMousseApprovalDialog = async (orderId) => {
    try {
      setSaving(true)

      const data = await getStockOrderById(orderId)
      const normalized = normalizeDetailPayload(data)
      const order = normalized.order

      const mappedItems = (normalized.items || []).map((item) => {
        const requestedQuantity = Number(item.requested_quantity || 0)

        const upstreamQuantity =
          item.maison_djo_approved_quantity !== null &&
          item.maison_djo_approved_quantity !== undefined
            ? Number(item.maison_djo_approved_quantity)
            : requestedQuantity

        const existingKinQuantity =
          item.kin_mousse_approved_quantity !== null &&
          item.kin_mousse_approved_quantity !== undefined
            ? Number(item.kin_mousse_approved_quantity)
            : upstreamQuantity

        return {
          item_id: item.id,
          product_code: item.product_code,
          product_name: item.product_name,
          requested_quantity: requestedQuantity,
          upstream_quantity: upstreamQuantity,
          approved_quantity: String(existingKinQuantity),
          supplier_unit_price: Number(item.supplier_unit_price || 0),
          shop_selling_price: Number(item.shop_selling_price || 0),
        }
      })

      setKinApprovalOrder(order || null)
      setKinApprovalItems(mappedItems)
      setKinApprovalNotes("")
      setOpenKinApproval(true)
    } catch (error) {
      console.error(error)
      showSnackbar(getApiErrorMessage(error), "error")
    } finally {
      setSaving(false)
    }
  }

  const closeKinMousseApprovalDialog = () => {
    if (saving) return

    setOpenKinApproval(false)
    setKinApprovalOrder(null)
    setKinApprovalItems([])
    setKinApprovalNotes("")
  }

  const updateKinApprovedQuantity = (itemId, value) => {
    setKinApprovalItems((prev) =>
      prev.map((item) =>
        String(item.item_id) === String(itemId)
          ? {
              ...item,
              approved_quantity: value,
            }
          : item
      )
    )
  }

  const confirmKinMousseApproval = async () => {
    if (!kinApprovalOrder?.id) {
      showSnackbar("Commande introuvable", "error")
      return
    }

    if (kinApprovalItems.length === 0) {
      showSnackbar("Aucun article à valider", "warning")
      return
    }

    let totalApproved = 0

    const items = kinApprovalItems.map((item) => {
      const approvedQuantity = Number(item.approved_quantity || 0)
      const upstreamQuantity = Number(item.upstream_quantity || 0)

      if (Number.isNaN(approvedQuantity)) {
        throw new Error(`Quantité invalide pour ${item.product_name}`)
      }

      if (approvedQuantity < 0) {
        throw new Error(
          `La quantité validée ne peut pas être négative pour ${item.product_name}`
        )
      }

      if (approvedQuantity > upstreamQuantity) {
        throw new Error(
          `La quantité validée pour ${item.product_name} ne peut pas dépasser ${upstreamQuantity}`
        )
      }

      totalApproved += approvedQuantity

      return {
        item_id: Number(item.item_id),
        approved_quantity: approvedQuantity,
      }
    })

    if (totalApproved <= 0) {
      showSnackbar(
        "Impossible de valider une commande avec quantité totale validée égale à 0. Rejetez plutôt la commande.",
        "warning"
      )
      return
    }

    try {
      setSaving(true)

      const result = await approveKinMousseStockOrder(
        kinApprovalOrder.id,
        kinApprovalNotes,
        items
      )

      showSnackbar(
        result?.message || "Validation effectuée avec succès",
        "success"
      )

      setOpenKinApproval(false)
      setKinApprovalOrder(null)
      setKinApprovalItems([])
      setKinApprovalNotes("")

      await loadOrders()

      if (detail?.order?.id) {
        const fresh = await getStockOrderById(detail.order.id)
        setDetail(normalizeDetailPayload(fresh))
      }
    } catch (error) {
      console.error(error)
      showSnackbar(getApiErrorMessage(error), "error")
    } finally {
      setSaving(false)
    }
  }

  const openShippingDialogForOrder = async (order) => {
    await loadShippingData()

    setShippingOrder(order)
    setShippingForm({
      vehicle_id: "",
      driver_id: "",
      estimated_distance_km: "",
      fuel_price_per_liter: String(DEFAULT_FUEL_PRICE_CDF),
      fuel_price_per_liter_cdf: String(DEFAULT_FUEL_PRICE_CDF),
      exchange_rate_cdf_usd: String(DEFAULT_EXCHANGE_RATE_CDF_USD),
      notes: "",
    })
    setOpenShipDialog(true)
  }

  const closeShippingDialog = () => {
    if (saving) return

    setOpenShipDialog(false)
    setShippingOrder(null)
    setShippingForm({
      vehicle_id: "",
      driver_id: "",
      estimated_distance_km: "",
      fuel_price_per_liter: String(DEFAULT_FUEL_PRICE_CDF),
      fuel_price_per_liter_cdf: String(DEFAULT_FUEL_PRICE_CDF),
      exchange_rate_cdf_usd: String(DEFAULT_EXCHANGE_RATE_CDF_USD),
      notes: "",
    })
  }

  const updateShippingForm = (field, value) => {
    setShippingForm((currentForm) => ({
      ...currentForm,
      [field]: value,
    }))
  }

  const confirmShipping = async () => {
    if (!shippingOrder?.id) {
      showSnackbar("Commande introuvable", "error")
      return
    }

    if (!shippingForm.vehicle_id) {
      showSnackbar("Sélectionnez le véhicule de livraison", "warning")
      return
    }

    if (!shippingForm.driver_id) {
      showSnackbar("Sélectionnez le chauffeur de livraison", "warning")
      return
    }

    if (Number(selectedShippingVehicle?.fuel_consumption_l_100km || 0) <= 0) {
      showSnackbar(
        "Ce véhicule n'a pas de consommation L/100km valide. Modifiez le véhicule avant expédition.",
        "warning"
      )
      return
    }

    if (Number(shippingForm.estimated_distance_km || 0) <= 0) {
      showSnackbar("La distance estimée en km est obligatoire.", "warning")
      return
    }

    if (Number(shippingForm.fuel_price_per_liter_cdf || 0) <= 0) {
      showSnackbar("Le prix du litre carburant en FC est obligatoire.", "warning")
      return
    }

    if (Number(shippingForm.exchange_rate_cdf_usd || 0) <= 0) {
      showSnackbar("Le taux FC/USD est obligatoire.", "warning")
      return
    }

    try {
      setSaving(true)

      const fuelPriceCdf = Number(shippingForm.fuel_price_per_liter_cdf || 0)
      const exchangeRate = Number(shippingForm.exchange_rate_cdf_usd || 0)

      const payload = {
        vehicle_id: Number(shippingForm.vehicle_id),
        driver_id: Number(shippingForm.driver_id),
        estimated_distance_km: Number(shippingForm.estimated_distance_km || 0),
        fuel_price_per_liter: fuelPriceCdf,
        fuel_price_per_liter_cdf: fuelPriceCdf,
        exchange_rate_cdf_usd: exchangeRate,
        notes: shippingForm.notes?.trim() || null,
      }

      const response = await api.post(
        `/stock-orders/${shippingOrder.id}/ship`,
        payload
      )

      const result = response.data

      showSnackbar(
        result?.trip_code
          ? `Commande expédiée. Tournée créée : ${result.trip_code}`
          : result?.message || "Commande expédiée avec succès",
        "success"
      )

      setOpenShipDialog(false)
      setShippingOrder(null)
      setShippingForm({
        vehicle_id: "",
        driver_id: "",
        estimated_distance_km: "",
        fuel_price_per_liter: String(DEFAULT_FUEL_PRICE_CDF),
        fuel_price_per_liter_cdf: String(DEFAULT_FUEL_PRICE_CDF),
        exchange_rate_cdf_usd: String(DEFAULT_EXCHANGE_RATE_CDF_USD),
        notes: "",
      })

      await loadOrders()
      await loadShippingData()

      if (detail?.order?.id) {
        const fresh = await getStockOrderById(detail.order.id)
        setDetail(normalizeDetailPayload(fresh))
      }
    } catch (error) {
      console.error(error)
      showSnackbar(getApiErrorMessage(error), "error")
    } finally {
      setSaving(false)
    }
  }

  const canApproveMaisonDjo = (order) => {
    return (
      order.status === "pending_maison_djo" &&
      isDistributorOrder(order) &&
      (isMaisonDjoUser || isKinMousseUser)
    )
  }

  const canApproveKinMousse = (order) => {
    if (order.status !== "pending_kin_mousse") {
      return false
    }

    if (isKinMousseUser) {
      return true
    }

    if (isDepotRelaisUser && isRelayDepotOrder(order)) {
      return true
    }

    return false
  }

  const canShip = (order) => {
    return order.status === "approved_by_kin_mousse" && isKinMousseUser
  }

  const canReceive = (order) => {
    if (order.status !== "shipped") return false

    if (isShopUser) {
      return String(order.request_store_id) === String(current.storeId)
    }

    if (isMaisonDjoUser) {
      return isDistributorOrder(order)
    }

    if (isDepotRelaisUser) {
      return isRelayDepotOrder(order)
    }

    return isKinMousseUser
  }

  return (
    <MainLayout>
      <Box sx={rlsysPageSx}>
      <Box sx={{ width: "100%", maxWidth: "100%", overflowX: "hidden" }}>
        <Box
          sx={{
            mb: 3,
            display: "flex",
            justifyContent: "space-between",
            alignItems: { xs: "stretch", md: "center" },
            flexDirection: { xs: "column", md: "row" },
            gap: 2,
          }}
        >
          <Box>
            <Typography variant="h4" fontWeight="bold">
              Commandes stock
            </Typography>

            <Typography variant="body2" color="text.secondary">
              Réapprovisionnement des {businessScopeLabel} depuis {MAIN_COMPANY}.
            </Typography>
          </Box>

          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={openCreateDialog}
            disabled={saving}
          >
            Nouvelle commande stock
          </Button>
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, minmax(0, 1fr))",
              md: "repeat(4, minmax(0, 1fr))",
            },
            gap: 1.1,
            mb: 1.6,
          }}
        >
          <MetricCard
            label="Commandes"
            value={filteredOrders.length}
            caption="Commandes visibles"
            accent="#2563eb"
            background="rgba(37, 99, 235, 0.09)"
            icon={<InventoryIcon fontSize="small" />}
          />

          {isShopUser ? (
            <>
              <MetricCard
                label="Quantité commandée"
                value={formatQuantity(shopOrderSummary.requestedQuantity)}
                caption={`Demandée par votre ${SHOP.toLowerCase()}`}
                accent="#0284c7"
                background="rgba(2, 132, 199, 0.09)"
                icon={<WarehouseIcon fontSize="small" />}
              />

              <MetricCard
                label="Valeur reçue"
                value={formatCurrency(shopOrderSummary.receivedValue, "USD")}
                caption="Produits déjà réceptionnés"
                accent="#16a34a"
                background="rgba(22, 163, 74, 0.09)"
                icon={<CheckCircleIcon fontSize="small" />}
              />

              <MetricCard
                label="Reste à recevoir"
                value={formatCurrency(shopOrderSummary.remainingValue, "USD")}
                caption="Valeur encore attendue"
                accent="#7c3aed"
                background="rgba(124, 58, 237, 0.09)"
                icon={<LocalShippingIcon fontSize="small" />}
              />
            </>
          ) : (
            <>
              <MetricCard
                label={`Valeur vente ${SHOP.toLowerCase()}`}
                value={formatCurrency(
                  filteredOrders.reduce(
                    (sum, item) => sum + getOrderShopAmount(item),
                    0
                  ),
                  "USD"
                )}
                caption="Valeur commerciale"
                accent="#0284c7"
                background="rgba(2, 132, 199, 0.09)"
                icon={<WarehouseIcon fontSize="small" />}
              />

              <MetricCard
                label={`Dette ${MAIN_COMPANY_SHORT}`}
                value={formatCurrency(
                  filteredOrders.reduce(
                    (sum, item) => sum + getOrderSupplierAmount(item),
                    0
                  ),
                  "USD"
                )}
                caption="Dette fournisseur attendue"
                accent="#d97706"
                background="rgba(217, 119, 6, 0.09)"
                icon={<LocalShippingIcon fontSize="small" />}
              />

              <MetricCard
                label="Marge attendue"
                value={formatCurrency(
                  filteredOrders.reduce(
                    (sum, item) => sum + getOrderMargin(item),
                    0
                  ),
                  "USD"
                )}
                caption="Marge commerciale estimée"
                accent="#16a34a"
                background="rgba(22, 163, 74, 0.09)"
                icon={<CheckCircleIcon fontSize="small" />}
              />
            </>
          )}
        </Box>

        <Paper variant="outlined" sx={{ p: 1.25, mb: 1.6, borderRadius: 3 }}>
          <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
            <TextField
              fullWidth
              placeholder={
                isShopUser
                  ? "Recherche par code ou statut..."
                  : `Recherche commande, ${SHOP.toLowerCase()}, organisation...`
              }
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />

            <TextField
              select
              label="Statut"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              sx={{ minWidth: { xs: "100%", md: 230 } }}
            >
              <MenuItem value="">Tous</MenuItem>
              {Object.entries(statusLabels).map(([value, label]) => (
                <MenuItem key={value} value={value}>
                  {label}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              label="Du"
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              slotProps={{
                inputLabel: { shrink: true },
              }}
              sx={{ minWidth: { xs: "100%", md: 160 } }}
            />

            <TextField
              label="Au"
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              slotProps={{
                inputLabel: { shrink: true },
              }}
              sx={{ minWidth: { xs: "100%", md: 160 } }}
            />

            <Button variant="contained" onClick={loadOrders} disabled={loading}>
              Appliquer
            </Button>

            <Button variant="outlined" onClick={resetFilters} disabled={loading}>
              Réinitialiser
            </Button>
          </Stack>
        </Paper>

        <Paper variant="outlined" sx={{ p: 1.15, mb: 1.6, borderRadius: 3 }}>
          <Stack
            direction="row"
            spacing={1}
            sx={{
              flexWrap: "wrap",
              gap: 1,
            }}
          >
            {statusGroupOptions.map((option) => (
              <Button
                key={option.value}
                size="small"
                variant={
                  statusGroupFilter === option.value ? "contained" : "outlined"
                }
                onClick={() => setStatusGroupFilter(option.value)}
              >
                {option.label} ({statusGroupCounts[option.value] || 0})
              </Button>
            ))}
          </Stack>

          {isShopUser && (
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              mt={1}
            >
              Historique commandes : utilisez “Historique reçues” pour revoir
              les commandes déjà réceptionnées dans votre {SHOP.toLowerCase()}.
            </Typography>
          )}
        </Paper>

        <Box sx={{ display: { xs: "block", md: "none" } }}>
          {loading ? (
            <Paper sx={{ p: 2, borderRadius: 3 }}>
              <Typography color="text.secondary">Chargement des commandes...</Typography>
            </Paper>
          ) : filteredOrders.length === 0 ? (
            <Paper sx={{ p: 2, borderRadius: 3 }}>
              <Typography color="text.secondary">
                Aucune commande stock trouvée.
              </Typography>
            </Paper>
          ) : (
            <Stack spacing={1.5}>
              {paginatedOrders.map((order) => {
                const allowDistributorApproval = canApproveMaisonDjo(order)
                const allowCompanyApproval = canApproveKinMousse(order)
                const allowShipping = canShip(order)
                const allowReception = canReceive(order)
                const allowReject = allowDistributorApproval || allowCompanyApproval

                return (
                  <MobileStockOrderCard
                    key={order.id}
                    order={order}
                    isShopUser={isShopUser}
                    saving={saving}
                    canValidateDistributor={allowDistributorApproval}
                    canValidateCompany={allowCompanyApproval}
                    canShipOrder={allowShipping}
                    canReceiveOrder={allowReception}
                    canRejectOrder={allowReject}
                    onDetail={() => openOrderDetail(order.id)}
                    onValidateDistributor={() =>
                      runAction(VALIDATION_DISTRIBUTOR, (notes) =>
                        approveMaisonDjoStockOrder(order.id, notes)
                      )
                    }
                    onValidateCompany={() => openKinMousseApprovalDialog(order.id)}
                    onShip={() => openShippingDialogForOrder(order)}
                    onReceive={() =>
                      runAction(`Réception ${SHOP.toLowerCase()}`, (notes) =>
                        receiveStockOrder(order.id, notes)
                      )
                    }
                    onReject={() =>
                      runAction("Rejet commande", (notes) =>
                        rejectStockOrder(order.id, notes)
                      )
                    }
                  />
                )
              })}
            </Stack>
          )}
        </Box>

        <Paper sx={{ borderRadius: 3.5, overflow: "hidden", display: { xs: "none", md: "block" } }}>
          <Box sx={{ overflowX: "auto" }}>
            <Table sx={{ minWidth: isShopUser ? 1380 : 1280 }}>
              <TableHead>
                {isShopUser ? (
                  <TableRow>
                    <TableCell>Code</TableCell>
                    <TableCell>Étape</TableCell>
                    <TableCell>Statut</TableCell>
                    <TableCell>Qté demandée</TableCell>
                    <TableCell>Qté validée</TableCell>
                    <TableCell>Qté expédiée</TableCell>
                    <TableCell>Qté reçue</TableCell>
                    <TableCell>Qté restante</TableCell>
                    <TableCell>Valeur reçue</TableCell>
                    <TableCell>Valeur restante</TableCell>
                    <TableCell>Dernier mouvement</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                ) : (
                  <TableRow>
                    <TableCell>Code</TableCell>
                    <TableCell>{SHOP}</TableCell>
                    <TableCell>Organisation</TableCell>
                    <TableCell>Statut</TableCell>
                    <TableCell>Qté</TableCell>
                    <TableCell>Valeur vente</TableCell>
                    <TableCell>Dette attendue</TableCell>
                    <TableCell>Marge attendue</TableCell>
                    <TableCell>Dernier mouvement</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                )}
              </TableHead>

              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={orderTableColSpan}>Chargement...</TableCell>
                  </TableRow>
                ) : filteredOrders.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={orderTableColSpan}>
                      Aucune commande stock trouvée
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedOrders.map((order) => (
                    <TableRow key={order.id}>
                      <TableCell>{order.order_code}</TableCell>

                      {isShopUser ? (
                        <>
                          <TableCell>
                            <Chip
                              label={getWorkflowLabel(order)}
                              color={getWorkflowColor(order)}
                              size="small"
                            />
                          </TableCell>

                          <TableCell>
                            <Chip
                              label={statusLabels[order.status] || order.status}
                              color={statusColors[order.status] || "default"}
                              size="small"
                            />
                          </TableCell>

                          <TableCell>
                            {formatQuantity(getOrderRequestedQuantity(order))}
                          </TableCell>

                          <TableCell>
                            {formatQuantity(getOrderApprovedQuantity(order))}
                          </TableCell>

                          <TableCell>
                            {formatQuantity(getOrderShippedQuantity(order))}
                          </TableCell>

                          <TableCell>
                            {formatQuantity(getOrderReceivedQuantity(order))}
                          </TableCell>

                          <TableCell>
                            {formatQuantity(getOrderRemainingQuantity(order))}
                          </TableCell>

                          <TableCell>
                            {formatAmount(getOrderReceivedShopValue(order))}
                          </TableCell>

                          <TableCell>
                            {formatAmount(getOrderRemainingShopValue(order))}
                          </TableCell>

                          <TableCell>
                            <Typography variant="body2">
                              {getLastMovementLabel(order).title}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {getLastMovementLabel(order).date}
                            </Typography>
                          </TableCell>
                        </>
                      ) : (
                        <>
                          <TableCell>
                            {getOrderStoreCode(order)} - {getOrderStoreName(order)}
                          </TableCell>

                          <TableCell>{getOrderOrganizationName(order)}</TableCell>

                          <TableCell>
                            <Chip
                              label={statusLabels[order.status] || order.status}
                              color={statusColors[order.status] || "default"}
                              size="small"
                            />
                          </TableCell>

                          <TableCell>{formatQuantity(getOrderQuantity(order))}</TableCell>

                          <TableCell>{formatAmount(getOrderShopAmount(order))}</TableCell>

                          <TableCell>
                            {formatAmount(getOrderSupplierAmount(order))}
                          </TableCell>

                          <TableCell>{formatAmount(getOrderMargin(order))}</TableCell>

                          <TableCell>
                            <Typography variant="body2">
                              {getLastMovementLabel(order).title}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {getLastMovementLabel(order).date}
                            </Typography>
                          </TableCell>
                        </>
                      )}

                      <TableCell>{formatDate(order.created_at)}</TableCell>

                      <TableCell>
                        <Stack
                          direction="row"
                          spacing={1}
                          sx={{
                            flexWrap: "wrap",
                            gap: 1,
                          }}
                        >
                          <Button
                            size="small"
                            variant="contained"
                            disabled={saving}
                            onClick={() => openOrderDetail(order.id)}
                          >
                            Détail
                          </Button>

                          {canApproveMaisonDjo(order) && (
                            <Button
                              size="small"
                              color="success"
                              variant="outlined"
                              disabled={saving}
                              onClick={() =>
                                runAction(VALIDATION_DISTRIBUTOR, (notes) =>
                                  approveMaisonDjoStockOrder(order.id, notes)
                                )
                              }
                            >
                              Valider
                            </Button>
                          )}

                          {canApproveKinMousse(order) && (
                            <Button
                              size="small"
                              color="primary"
                              variant="outlined"
                              disabled={saving}
                              onClick={() => openKinMousseApprovalDialog(order.id)}
                            >
                              Valider
                            </Button>
                          )}

                          {canShip(order) && (
                            <Button
                              size="small"
                              color="secondary"
                              variant="outlined"
                              disabled={saving}
                              onClick={() => openShippingDialogForOrder(order)}
                            >
                              Expédier
                            </Button>
                          )}

                          {canReceive(order) && (
                            <Button
                              size="small"
                              color="success"
                              variant="contained"
                              disabled={saving}
                              onClick={() =>
                                runAction(`Réception ${SHOP.toLowerCase()}`, (notes) =>
                                  receiveStockOrder(order.id, notes)
                                )
                              }
                            >
                              Recevoir
                            </Button>
                          )}

                          {(canApproveMaisonDjo(order) ||
                            canApproveKinMousse(order)) && (
                            <Button
                              size="small"
                              color="error"
                              variant="outlined"
                              disabled={saving}
                              onClick={() =>
                                runAction("Rejet commande", (notes) =>
                                  rejectStockOrder(order.id, notes)
                                )
                              }
                            >
                              Rejeter
                            </Button>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Box>
        </Paper>

        <TablePagination
          component="div"
          count={filteredOrders.length}
          page={orderPage}
          onPageChange={(_, newPage) => setOrderPage(newPage)}
          rowsPerPage={ROWS_PER_PAGE}
          rowsPerPageOptions={[ROWS_PER_PAGE]}
          labelRowsPerPage=""
          labelDisplayedRows={({ from, to, count }) =>
            `${from}–${to} sur ${count}`
          }
          sx={{
            mt: 1,
            bgcolor: "#ffffff",
            border: "1px solid rgba(148, 163, 184, 0.22)",
            borderRadius: 2.5,
            "& .MuiTablePagination-toolbar": {
              minHeight: 50,
              px: { xs: 1, sm: 2 },
            },
            "& .MuiTablePagination-selectLabel": {
              display: "none",
            },
            "& .MuiTablePagination-input": {
              display: "none",
            },
          }}
        />
      </Box>

      <Dialog
        open={openCreate}
        onClose={closeCreateDialog}
        fullWidth
        maxWidth="md"
        fullScreen={isSmDown}
        PaperProps={{
          sx: {
            borderRadius: { xs: 0, sm: 3 },
          },
        }}
      >
        <DialogTitle>Nouvelle commande de réapprovisionnement</DialogTitle>

        <DialogContent>
          <Stack spacing={2} mt={1}>
            <Alert severity="info" variant="outlined">
              Le {SHOP.toLowerCase()} crée la commande. Pour {MAIN_DISTRIBUTOR},
              la commande passe par {VALIDATION_DISTRIBUTOR.toLowerCase()}. Pour{" "}
              {RELAY_DEPOT}, elle va directement en validation côté{" "}
              {MAIN_COMPANY}. Le stock augmente seulement après réception
              confirmée.
            </Alert>

            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <TextField
                select
                label={`${SHOP} demandeur`}
                value={formData.request_store_id}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    request_store_id: e.target.value,
                  }))
                }
                disabled={isShopUser || saving}
                fullWidth
              >
                {stores.map((store) => (
                  <MenuItem key={store.id} value={store.id}>
                    {store.code} - {store.name}
                  </MenuItem>
                ))}
              </TextField>

              {!isShopUser && (
                <TextField
                  select
                  label={SOURCE_WAREHOUSE}
                  value={formData.source_warehouse_id}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      source_warehouse_id: e.target.value,
                    }))
                  }
                  disabled={saving}
                  fullWidth
                >
                  <MenuItem value="">À compléter par {MAIN_COMPANY_SHORT}</MenuItem>
                  {warehouses.map((warehouse) => (
                    <MenuItem key={warehouse.id} value={warehouse.id}>
                      {warehouse.code} - {warehouse.name}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            </Stack>

            <TextField
              label="Notes"
              multiline
              minRows={2}
              value={formData.notes}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  notes: e.target.value,
                }))
              }
              disabled={saving}
              fullWidth
            />

            <Divider />

            <Typography variant="h6" fontWeight={900}>
              Ajouter produit
            </Typography>

            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <Autocomplete
                options={products}
                value={findProduct(products, line.product_id)}
                onChange={(_, product) =>
                  setLine((prev) => ({
                    ...prev,
                    product_id: product?.id ? String(product.id) : "",
                  }))
                }
                getOptionLabel={getProductSearchLabel}
                isOptionEqualToValue={(option, value) =>
                  String(option.id) === String(value.id)
                }
                renderOption={(props, product) =>
                  renderProductOption(props, product, canSeeSupplierPrices)
                }
                noOptionsText="Aucun produit trouvé"
                disabled={saving}
                fullWidth
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Produit"
                    placeholder="Tapez le code ou le nom du produit..."
                  />
                )}
              />

              <TextField
                label="Quantité"
                type="number"
                value={line.requested_quantity}
                onChange={(e) =>
                  setLine((prev) => ({
                    ...prev,
                    requested_quantity: e.target.value,
                  }))
                }
                disabled={saving}
                slotProps={{
                  htmlInput: { min: 1 },
                }}
                sx={{ minWidth: { xs: "100%", md: 180 } }}
              />

              <Button variant="contained" onClick={addItem} disabled={saving}>
                Ajouter
              </Button>
            </Stack>

            <Box sx={{ overflowX: "auto" }}>
              <Table sx={{ minWidth: canSeeSupplierPrices ? 950 : 650 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Code</TableCell>
                    <TableCell>Produit</TableCell>
                    <TableCell>Qté</TableCell>
                    {canSeeSupplierPrices && <TableCell>{COMPANY_PRICE}</TableCell>}
                    <TableCell>Prix vente</TableCell>
                    <TableCell>Valeur vente</TableCell>
                    {canSeeSupplierPrices && <TableCell>Dette</TableCell>}
                    {canSeeSupplierPrices && <TableCell>Marge</TableCell>}
                    <TableCell>Action</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {cart.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={cartTableColSpan}>
                        Aucun produit ajouté
                      </TableCell>
                    </TableRow>
                  ) : (
                    cart.map((item) => (
                      <TableRow key={item.product_id}>
                        <TableCell>{item.product_code}</TableCell>
                        <TableCell>{item.product_name}</TableCell>
                        <TableCell>{item.requested_quantity}</TableCell>

                        {canSeeSupplierPrices && (
                          <TableCell>
                            <PriceStack
                              usd={item.supplier_unit_price}
                              cdf={item.supplier_unit_price_cdf}
                            />
                          </TableCell>
                        )}

                        <TableCell>
                          <PriceStack
                            usd={item.shop_selling_price}
                            cdf={item.shop_selling_price_cdf}
                          />
                        </TableCell>
                        <TableCell>
                          <PriceStack usd={item.shop_total} cdf={item.shop_total_cdf} />
                        </TableCell>

                        {canSeeSupplierPrices && (
                          <TableCell>
                            <PriceStack usd={item.supplier_total} cdf={item.supplier_total_cdf} />
                          </TableCell>
                        )}

                        {canSeeSupplierPrices && (
                          <TableCell>
                            <PriceStack usd={item.margin} cdf={item.margin_cdf} />
                          </TableCell>
                        )}

                        <TableCell>
                          <Button
                            color="error"
                            size="small"
                            disabled={saving}
                            onClick={() => removeItem(item.product_id)}
                          >
                            Retirer
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </Box>

            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <Typography>
                Quantité totale : <strong>{totals.quantity}</strong>
              </Typography>

              <Typography>
                Valeur commande au prix de vente :{" "}
                <strong>{formatUsdCdf(totals.shop, totals.shopCdf)}</strong>
              </Typography>

              {canSeeSupplierPrices && (
                <>
                  <Typography>
                    Dette attendue envers {MAIN_COMPANY} :{" "}
                    <strong>{formatUsdCdf(totals.supplier, totals.supplierCdf)}</strong>
                  </Typography>

                  <Typography>
                    Marge attendue : <strong>{formatUsdCdf(totals.margin, totals.marginCdf)}</strong>
                  </Typography>
                </>
              )}
            </Paper>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeCreateDialog} disabled={saving}>
            Annuler
          </Button>

          <Button variant="contained" onClick={handleCreate} disabled={saving}>
            {saving ? "Enregistrement..." : "Créer commande"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openShipDialog}
        onClose={closeShippingDialog}
        fullWidth
        maxWidth="md"
        fullScreen={isSmDown}
        PaperProps={{
          sx: {
            borderRadius: { xs: 0, sm: 3 },
          },
        }}
      >
        <DialogTitle>
          Expédier commande {shippingOrder?.order_code || ""}
        </DialogTitle>

        <DialogContent>
          <Stack spacing={2} mt={1}>
            <Alert severity="info" variant="outlined">
              Sélectionnez le véhicule et le chauffeur. À la confirmation, le
              véhicule passe en livraison, le chauffeur passe en mission et une
              tournée est créée automatiquement.
            </Alert>

            {shippingOrder && (
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Typography>
                  {SHOP} :{" "}
                  <strong>
                    {getOrderStoreCode(shippingOrder)} -{" "}
                    {getOrderStoreName(shippingOrder)}
                  </strong>
                </Typography>

                <Typography>
                  Organisation :{" "}
                  <strong>{getOrderOrganizationName(shippingOrder)}</strong>
                </Typography>

                <Typography>
                  Quantité : <strong>{getOrderQuantity(shippingOrder)}</strong>
                </Typography>

                <Typography>
                  Valeur vente :{" "}
                  <strong>{formatAmount(getOrderShopAmount(shippingOrder))}</strong>
                </Typography>
              </Paper>
            )}

            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <Autocomplete
                options={shippingData.vehicles}
                value={selectedShippingVehicle}
                onChange={(_, value) =>
                  updateShippingForm("vehicle_id", value?.id || "")
                }
                disabled={saving}
                getOptionLabel={(vehicle) =>
                  vehicle
                    ? `${vehicle.plate_number || ""} - ${vehicle.brand || ""} ${
                        vehicle.model || ""
                      } | ${vehicle.fuel_consumption_l_100km || 0} L/100km`
                    : ""
                }
                isOptionEqualToValue={(option, value) =>
                  String(option.id) === String(value.id)
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Véhicule disponible"
                    placeholder="Tapez plaque, marque ou modèle..."
                    required
                  />
                )}
                fullWidth
              />

              <Autocomplete
                options={shippingData.drivers}
                value={selectedShippingDriver}
                onChange={(_, value) =>
                  updateShippingForm("driver_id", value?.id || "")
                }
                disabled={saving}
                getOptionLabel={(driver) =>
                  driver
                    ? `${driver.full_name || ""} - ${
                        driver.license_number || "Permis N/D"
                      }`
                    : ""
                }
                isOptionEqualToValue={(option, value) =>
                  String(option.id) === String(value.id)
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Chauffeur disponible"
                    placeholder="Tapez le nom du chauffeur..."
                    required
                  />
                )}
                fullWidth
              />
            </Stack>

            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <TextField
                label="Distance estimée km"
                type="number"
                value={shippingForm.estimated_distance_km}
                onChange={(event) =>
                  updateShippingForm("estimated_distance_km", event.target.value)
                }
                disabled={saving}
                fullWidth
                required
                slotProps={{
                  htmlInput: {
                    min: 0,
                    step: "0.1",
                  },
                }}
              />

              <TextField
                label="Prix litre carburant FC"
                type="number"
                value={shippingForm.fuel_price_per_liter_cdf}
                onChange={(event) => {
                  updateShippingForm("fuel_price_per_liter_cdf", event.target.value)
                  updateShippingForm("fuel_price_per_liter", event.target.value)
                }}
                disabled={saving}
                fullWidth
                required
                slotProps={{
                  htmlInput: {
                    min: 0,
                    step: "1",
                  },
                }}
              />

              <TextField
                label="Taux FC / USD"
                type="number"
                value={shippingForm.exchange_rate_cdf_usd}
                onChange={(event) =>
                  updateShippingForm("exchange_rate_cdf_usd", event.target.value)
                }
                disabled={saving}
                fullWidth
                required
                slotProps={{
                  htmlInput: {
                    min: 0,
                    step: "1",
                  },
                }}
              />
            </Stack>

            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              {selectedShippingVehicle &&
                Number(selectedShippingVehicle.fuel_consumption_l_100km || 0) <= 0 && (
                  <Alert severity="warning" sx={{ mb: 1 }}>
                    Ce véhicule affiche 0 L/100km. Corrigez la consommation dans
                    la fiche véhicule avant de confirmer l’expédition.
                  </Alert>
                )}

              <Typography>
                Carburant estimé :{" "}
                <strong>{formatQuantity(shippingFuelEstimate.liters)} L</strong>
              </Typography>

              <Typography>
                Coût carburant estimé FC :{" "}
                <strong>{formatCurrency(shippingFuelEstimate.costCdf, "CDF")}</strong>
              </Typography>

              <Typography>
                Coût carburant estimé USD :{" "}
                <strong>{formatCurrency(shippingFuelEstimate.costUsd, "USD")}</strong>
              </Typography>

              <Typography variant="caption" color="text.secondary">
                Formule : distance × consommation L/100km ÷ 100 × prix litre FC.
              </Typography>
            </Paper>

            <TextField
              label="Notes expédition"
              multiline
              minRows={3}
              value={shippingForm.notes}
              onChange={(event) =>
                updateShippingForm("notes", event.target.value)
              }
              disabled={saving}
              fullWidth
              placeholder={`Exemple : Véhicule sorti pour livraison ${SHOP.toLowerCase()}.`}
            />
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeShippingDialog} disabled={saving}>
            Annuler
          </Button>

          <Button
            variant="contained"
            color="secondary"
            onClick={confirmShipping}
            disabled={saving}
          >
            {saving ? "Expédition..." : "Confirmer expédition"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openKinApproval}
        onClose={closeKinMousseApprovalDialog}
        fullWidth
        maxWidth="lg"
        fullScreen={isSmDown}
        PaperProps={{
          sx: {
            borderRadius: { xs: 0, sm: 3 },
          },
        }}
      >
        <DialogTitle>
          Validation commande {kinApprovalOrder?.order_code || ""}
        </DialogTitle>

        <DialogContent>
          <Stack spacing={2} mt={1}>
            <Alert severity="warning" variant="outlined">
              La quantité validée peut être réduite selon le stock disponible.
              La quantité demandée reste conservée pour la traçabilité. Le stock
              du {SHOP.toLowerCase()} augmentera seulement après expédition et
              réception.
            </Alert>

            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <Typography>
                {SHOP} :{" "}
                <strong>
                  {getOrderStoreCode(kinApprovalOrder)} -{" "}
                  {getOrderStoreName(kinApprovalOrder)}
                </strong>
              </Typography>

              <Typography>
                Organisation :{" "}
                <strong>{getOrderOrganizationName(kinApprovalOrder)}</strong>
              </Typography>

              <Typography>
                Statut :{" "}
                <strong>
                  {statusLabels[kinApprovalOrder?.status] ||
                    kinApprovalOrder?.status ||
                    "-"}
                </strong>
              </Typography>
            </Stack>

            <TextField
              label="Notes validation"
              multiline
              minRows={2}
              value={kinApprovalNotes}
              onChange={(e) => setKinApprovalNotes(e.target.value)}
              disabled={saving}
              fullWidth
              placeholder="Exemple : Stock insuffisant, quantité réduite selon disponibilité."
            />

            <Box sx={{ overflowX: "auto" }}>
              <Table sx={{ minWidth: 1150 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Produit</TableCell>
                    <TableCell>Demandé {SHOP.toLowerCase()}</TableCell>
                    <TableCell>Qté max validable</TableCell>
                    <TableCell>Validé</TableCell>
                    <TableCell>{COMPANY_PRICE}</TableCell>
                    <TableCell>Prix vente</TableCell>
                    <TableCell>Dette {MAIN_COMPANY_SHORT}</TableCell>
                    <TableCell>Valeur vente</TableCell>
                    <TableCell>Marge</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {kinApprovalItems.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9}>Aucun article</TableCell>
                    </TableRow>
                  ) : (
                    kinApprovalItems.map((item) => {
                      const approvedQuantity = Number(item.approved_quantity || 0)
                      const supplierPrice = Number(item.supplier_unit_price || 0)
                      const shopPrice = Number(item.shop_selling_price || 0)

                      const supplierTotal = approvedQuantity * supplierPrice
                      const shopTotal = approvedQuantity * shopPrice
                      const margin = shopTotal - supplierTotal

                      return (
                        <TableRow key={item.item_id}>
                          <TableCell>
                            {item.product_code} - {item.product_name}
                          </TableCell>

                          <TableCell>{item.requested_quantity}</TableCell>

                          <TableCell>{item.upstream_quantity}</TableCell>

                          <TableCell>
                            <TextField
                              type="number"
                              size="small"
                              value={item.approved_quantity}
                              onChange={(e) =>
                                updateKinApprovedQuantity(
                                  item.item_id,
                                  e.target.value
                                )
                              }
                              disabled={saving}
                              slotProps={{
                                htmlInput: {
                                  min: 0,
                                  max: item.upstream_quantity,
                                  step: "0.01",
                                },
                              }}
                              sx={{ width: 150 }}
                            />
                          </TableCell>

                          <TableCell>{formatAmount(supplierPrice)}</TableCell>
                          <TableCell>{formatAmount(shopPrice)}</TableCell>
                          <TableCell>{formatAmount(supplierTotal)}</TableCell>
                          <TableCell>{formatAmount(shopTotal)}</TableCell>
                          <TableCell>{formatAmount(margin)}</TableCell>
                        </TableRow>
                      )
                    })
                  )}
                </TableBody>
              </Table>
            </Box>

            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <Typography>
                Quantité totale validée :{" "}
                <strong>{kinApprovalTotals.quantity}</strong>
              </Typography>

              <Typography>
                Dette attendue envers {MAIN_COMPANY} :{" "}
                <strong>{formatAmount(kinApprovalTotals.supplier)}</strong>
              </Typography>

              <Typography>
                Valeur vente : <strong>{formatAmount(kinApprovalTotals.shop)}</strong>
              </Typography>

              <Typography>
                Marge attendue :{" "}
                <strong>{formatAmount(kinApprovalTotals.margin)}</strong>
              </Typography>
            </Paper>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeKinMousseApprovalDialog} disabled={saving}>
            Annuler
          </Button>

          <Button
            variant="contained"
            color="primary"
            onClick={confirmKinMousseApproval}
            disabled={saving}
          >
            {saving ? "Validation..." : "Confirmer validation"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openDetail}
        onClose={() => setOpenDetail(false)}
        fullWidth
        maxWidth="lg"
        fullScreen={isSmDown}
        PaperProps={{
          sx: {
            borderRadius: { xs: 0, sm: 3 },
          },
        }}
      >
        <DialogTitle>
          Détail commande {detail?.order?.order_code || ""}
        </DialogTitle>

        <DialogContent>
          {!detail?.order ? (
            <Typography>Chargement...</Typography>
          ) : (
            <Stack spacing={2} mt={1}>
              <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                <Chip
                  label={statusLabels[detail.order.status] || detail.order.status}
                  color={statusColors[detail.order.status] || "default"}
                />

                <Typography>
                  {SHOP} : <strong>{getOrderStoreName(detail.order)}</strong>
                </Typography>

                {!isShopUser && (
                  <Typography>
                    Organisation :{" "}
                    <strong>{getOrderOrganizationName(detail.order)}</strong>
                  </Typography>
                )}
              </Stack>

              {isShopUser && (
                <Alert severity="info" variant="outlined">
                  Cette vue présente uniquement les quantités et les valeurs de
                  vente utiles au suivi de votre {SHOP.toLowerCase()}. Les prix
                  entreprise, la dette fournisseur et la marge sont réservés à la
                  gestion.
                </Alert>
              )}

              {isShopUser && (
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                  <Stack
                    direction={{ xs: "column", md: "row" }}
                    spacing={3}
                    sx={{
                      flexWrap: "wrap",
                      gap: 1,
                    }}
                  >
                    <Typography>
                      Étape : <strong>{getWorkflowLabel(detail.order)}</strong>
                    </Typography>
                    <Typography>
                      Dernier mouvement :{" "}
                      <strong>{getLastMovementLabel(detail.order).title}</strong>
                    </Typography>
                    <Typography>
                      Date mouvement :{" "}
                      <strong>{getLastMovementLabel(detail.order).date}</strong>
                    </Typography>
                    <Typography>
                      Nombre de mouvements :{" "}
                      <strong>
                        {detail.order.history_count || detail.history.length}
                      </strong>
                    </Typography>
                  </Stack>
                </Paper>
              )}

              <Divider />

              <Box sx={{ overflowX: "auto" }}>
                <Table sx={{ minWidth: detailTableMinWidth }}>
                  <TableHead>
                    {isShopUser ? (
                      <TableRow>
                        <TableCell>Produit</TableCell>
                        <TableCell>Demandé</TableCell>
                        <TableCell>Validé</TableCell>
                        <TableCell>Expédié</TableCell>
                        <TableCell>Reçu</TableCell>
                        <TableCell>Restant</TableCell>
                        <TableCell>Prix vente</TableCell>
                        <TableCell>Valeur reçue</TableCell>
                        <TableCell>Valeur restante</TableCell>
                      </TableRow>
                    ) : (
                      <TableRow>
                        <TableCell>Produit</TableCell>
                        <TableCell>Demandé</TableCell>
                        <TableCell>Validé {MAIN_DISTRIBUTOR}</TableCell>
                        <TableCell>Validé</TableCell>
                        <TableCell>Expédié</TableCell>
                        <TableCell>Reçu</TableCell>
                        <TableCell>Prix vente</TableCell>
                        <TableCell>Valeur vente</TableCell>
                        <TableCell>{COMPANY_PRICE}</TableCell>
                        <TableCell>Dette</TableCell>
                        <TableCell>Marge</TableCell>
                      </TableRow>
                    )}
                  </TableHead>

                  <TableBody>
                    {(detail.items || []).length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={isShopUser ? 9 : 11}>
                          Aucun article dans cette commande
                        </TableCell>
                      </TableRow>
                    ) : (
                      (detail.items || []).map((item) => (
                        <TableRow key={item.id}>
                          <TableCell>
                            {item.product_code} - {item.product_name}
                          </TableCell>

                          <TableCell>
                            {Number(item.requested_quantity || 0)}
                          </TableCell>

                          {isShopUser ? (
                            <>
                              <TableCell>
                                {item.kin_mousse_approved_quantity ??
                                  item.maison_djo_approved_quantity ??
                                  "-"}
                              </TableCell>

                              <TableCell>
                                {item.shipped_quantity ?? "-"}
                              </TableCell>

                              <TableCell>
                                {item.received_quantity ?? "-"}
                              </TableCell>

                              <TableCell>
                                {getItemRemainingQuantity(
                                  item,
                                  detail.order.status
                                )}
                              </TableCell>

                              <TableCell>
                                {formatAmount(item.shop_selling_price)}
                              </TableCell>

                              <TableCell>
                                {formatAmount(getItemReceivedShopValue(item))}
                              </TableCell>

                              <TableCell>
                                {formatAmount(
                                  getItemRemainingShopValue(
                                    item,
                                    detail.order.status
                                  )
                                )}
                              </TableCell>
                            </>
                          ) : (
                            <>
                              <TableCell>
                                {item.maison_djo_approved_quantity ?? "-"}
                              </TableCell>

                              <TableCell>
                                {item.kin_mousse_approved_quantity ?? "-"}
                              </TableCell>

                              <TableCell>
                                {item.shipped_quantity ?? "-"}
                              </TableCell>

                              <TableCell>
                                {item.received_quantity ?? "-"}
                              </TableCell>

                              <TableCell>
                                {formatAmount(item.shop_selling_price)}
                              </TableCell>

                              <TableCell>
                                {formatAmount(item.shop_total_amount)}
                              </TableCell>

                              <TableCell>
                                {formatAmount(item.supplier_unit_price)}
                              </TableCell>

                              <TableCell>
                                {formatAmount(item.supplier_total_amount)}
                              </TableCell>

                              <TableCell>
                                {formatAmount(item.expected_margin)}
                              </TableCell>
                            </>
                          )}
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </Box>

              {canApproveKinMousse(detail.order) && (
                <Box>
                  <Button
                    variant="contained"
                    onClick={() => {
                      setOpenDetail(false)
                      openKinMousseApprovalDialog(detail.order.id)
                    }}
                    disabled={saving}
                  >
                    Valider avec modification quantité
                  </Button>
                </Box>
              )}

              <Divider />

              <Typography variant="h6" fontWeight={900}>
                Historique
              </Typography>

              {(detail.history || []).length === 0 ? (
                <Typography color="text.secondary">Aucun historique</Typography>
              ) : (
                (detail.history || []).map((row) => (
                  <Paper
                    key={row.id}
                    variant="outlined"
                    sx={{ p: 1.5, borderRadius: 2 }}
                  >
                    <Typography variant="body2">
                      <strong>{statusLabels[row.new_status] || row.new_status}</strong>
                      {" — "}
                      {row.changed_by_name || "Utilisateur"}
                    </Typography>

                    <Typography variant="caption" color="text.secondary">
                      {row.changed_at
                        ? new Date(row.changed_at).toLocaleString()
                        : ""}
                    </Typography>

                    {(row.comment || row.notes) && (
                      <Typography variant="body2" mt={0.5} whiteSpace="pre-line">
                        {row.comment || row.notes}
                      </Typography>
                    )}
                  </Paper>
                ))
              )}
            </Stack>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpenDetail(false)}>Fermer</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          onClose={handleCloseSnackbar}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
          </Box>
    </MainLayout>
  )
}

export default StockOrdersPage